import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import RestoreOrder from './RestoreOrder';
import CopyOrder from './CopyOrder';

function OrdersManagement() {
  const { schoolId } = useParams();
  const apiBase = `/api/admin/${schoolId}`;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/orders`);
      setOrders(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load orders');
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const sendUnsentEmails = async () => {
    const unsentCount = orders.filter(order => !order.email_sent && order.email).length;

    if (unsentCount === 0) {
      alert('There are no unsent emails.');
      return;
    }

    const confirmed = window.confirm(
      `Send confirmation emails to ${unsentCount} order(s) that have not yet received one?`
    );
    if (!confirmed) return;

    setSendingEmails(true);
    try {
      const response = await axios.post(`${apiBase}/orders/send-unsent-emails`);
      alert(response.data.message);
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send emails';
      const sent = err.response?.data?.sent;
      alert(sent !== undefined ? `${msg} (${sent} sent before failure)` : msg);
      fetchOrders();
    } finally {
      setSendingEmails(false);
    }
  };

  const deletePaidOrders = async () => {
    const paidOrdersCount = orders.filter(order => order.paid).length;

    if (paidOrdersCount === 0) {
      alert('There are no paid orders to delete.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ALL ${paidOrdersCount} paid orders?\n\n` +
      `This will hide all orders that have been marked as paid from the system.\n` +
      `The order records will be preserved in the database but won't appear in the active orders list.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await axios.delete(`${apiBase}/orders/paid/bulk`);
      alert(`Successfully deleted ${response.data.deleted_count} paid orders.`);
      // Refresh orders
      fetchOrders();
    } catch (err) {
      alert('Failed to delete paid orders');
    }
  };

  if (loading) return <div>Loading orders...</div>;
  if (error) return <div>Error: {error}</div>;

  const paidOrdersCount = orders.filter(order => order.paid).length;
  const unsentOrders = orders.filter(order => !order.email_sent && order.email);
  const unsentEmailCount = unsentOrders.length;

  return (
    <div className="orders-management">
      <div style={{
        marginBottom: '1rem'
      }}>
        <h2 style={{margin: 0}}>Orders Management</h2>
      </div>

      {/* Send Unsent Emails Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{margin: '0 0 1rem 0'}}>Send Confirmation Emails</h3>

        {unsentEmailCount === 0 ? (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724'
          }}>
            <strong>All emails sent</strong>
            <div style={{fontSize: '0.9rem', marginTop: '0.5rem'}}>
              Every order with an email address has received a confirmation.
            </div>
          </div>
        ) : (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }}>
            <div style={{marginBottom: '1rem'}}>
              <strong>{unsentEmailCount} order{unsentEmailCount !== 1 ? 's' : ''} will receive a confirmation email:</strong>
            </div>

            <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '1rem', fontSize: '0.9rem'}}>
              <thead>
                <tr style={{backgroundColor: '#ffeeba'}}>
                  <th style={{padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #ffc107'}}>Ref #</th>
                  <th style={{padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #ffc107'}}>Name</th>
                  <th style={{padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #ffc107'}}>Email Address</th>
                  <th style={{padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #ffc107'}}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {unsentOrders.map((order, i) => (
                  <tr key={order.id} style={{backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.03)'}}>
                    <td style={{padding: '0.4rem 0.75rem', borderBottom: '1px solid #fde8a0', fontFamily: 'monospace'}}>{order.reference_number}</td>
                    <td style={{padding: '0.4rem 0.75rem', borderBottom: '1px solid #fde8a0'}}>{order.name}</td>
                    <td style={{padding: '0.4rem 0.75rem', borderBottom: '1px solid #fde8a0'}}>{order.email}</td>
                    <td style={{padding: '0.4rem 0.75rem', borderBottom: '1px solid #fde8a0'}}>{order.grade}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <button
                onClick={sendUnsentEmails}
                disabled={sendingEmails}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: sendingEmails ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: sendingEmails ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {sendingEmails ? 'Sending...' : `Send ${unsentEmailCount} Email${unsentEmailCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>

      <CopyOrder apiBase={apiBase} onCopied={fetchOrders} />

      <RestoreOrder apiBase={apiBase} onRestored={fetchOrders} />

      {/* Delete Paid Orders Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{margin: '0 0 1rem 0'}}>Delete Paid Orders</h3>

        {paidOrdersCount === 0 ? (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d1ecf1',
            border: '1px solid #bee5eb',
            borderRadius: '4px',
            color: '#0c5460'
          }}>
            <strong>No paid orders to delete</strong>
            <div style={{fontSize: '0.9rem', marginTop: '0.5rem'}}>
              There are currently no orders marked as paid in the system.
            </div>
          </div>
        ) : (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }}>
            <div style={{marginBottom: '1rem'}}>
              <strong>Archive Paid Orders</strong>
              <div style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>
                This will archive all orders marked as paid. Archived orders are hidden from the active orders list but preserved in the database.
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '1rem'
            }}>
              <div>
                <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545'}}>
                  {paidOrdersCount}
                </div>
                <div style={{fontSize: '0.9rem', color: '#666'}}>
                  paid orders will be deleted
                </div>
              </div>

              <button
                onClick={deletePaidOrders}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Delete All Paid Orders
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Information Section */}
      <div style={{
        backgroundColor: '#e7f3ff',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid #b3d9ff'
      }}>
        <h4 style={{margin: '0 0 0.5rem 0', color: '#004085'}}>ℹ️ About Orders Management</h4>
        <ul style={{margin: '0.5rem 0 0 1.5rem', fontSize: '0.9rem', color: '#004085'}}>
          <li>Use this page to perform bulk operations on orders</li>
          <li>Deleted orders are archived (soft deleted) and hidden from the active orders list</li>
          <li>Archived order records are preserved in the database for record-keeping</li>
          <li>Always ensure orders have been fulfilled before archiving them</li>
        </ul>
      </div>
    </div>
  );
}

export default OrdersManagement;
